import { FoodsIcon } from "@foodpilot/foods";
import ContentCopy from "@mui/icons-material/ContentCopy";
import Delete from "@mui/icons-material/Delete";
import Edit from "@mui/icons-material/Edit";
import { useTranslation } from "react-i18next";
import { useQueryClient } from "react-query";
import { useNavigate, useLocation } from "react-router";
import { useProductDelete } from "src/api/client-api/products/useDeleteProduct";
import { useCloneProduct } from "src/api/client-api/products/useCloneProduct";
import { GridProduct, productGridKey } from "src/api/client-api/products/useGetAllproduct";
import { MenuItemType } from "src/components/ActionsPopover/ActionsPopover.types";
import { CanUserCallback, CREATE, PRODUCT } from "src/context/FoodpilotContext";
import { URLHandler } from "src/services/URLHandler";

type MenuItemsOptions = {
    companyIds: {
        "@id": IRI;
        id: number;
    };
    canUser: CanUserCallback;
};

export const useProductMenuItems = (options: MenuItemsOptions) => {
    const { canUser, companyIds } = options;

    const { t } = useTranslation();
    const cloneProduct = useCloneProduct();
    const deleteProduct = useProductDelete();
    const queryClient = useQueryClient();
    const navigate = useNavigate();
    const location = useLocation();

    const currentUrl = location.pathname + location.search;

    const menuItemsAcc: MenuItemType<GridProduct>[] = [];

    menuItemsAcc.push({
        icon: <FoodsIcon icon="scoresIndicators" size={3} />,
        label: t("scores_indicateurs_cles"),
        onClick: (entity) => {
            const url = URLHandler.product.scores(companyIds.id, entity.id);
            navigate(`${url}?returnUrl=${encodeURIComponent(currentUrl)}`);
        },
        shouldShow: (entity) => {
            return entity?.canView ?? false;
        },
    });
    menuItemsAcc.push({
        icon: <FoodsIcon icon="questionnaires" size={3} />,
        label: t("questionnaire"),
        onClick: (entity) => {
            const url = URLHandler.product.ssq(companyIds.id, entity.id);
            navigate(`${url}?returnUrl=${encodeURIComponent(currentUrl)}`);
        },
        shouldShow: (entity) => {
            return entity?.canView ?? false;
        },
    });
    menuItemsAcc.push({
        icon: <FoodsIcon icon="informations" size={3} />,
        label: t("informations"),
        onClick: (entity) => {
            const url = URLHandler.product.information(companyIds.id, entity.id);
            navigate(`${url}?returnUrl=${encodeURIComponent(currentUrl)}`);
        },
        shouldShow: (entity) => {
            return entity?.canView ?? false;
        },
    });

    menuItemsAcc.push({
        icon: <ContentCopy />,
        label: t("dupliquer"),
        onClick: (entity) => {
            if (entity !== undefined) {
                const options = {
                    productId: entity.id,
                    ownedById: companyIds.id,
                    suffix: t("copy"),
                };
                cloneProduct.mutate(options, {
                    onSuccess: () => {
                        queryClient.invalidateQueries([productGridKey]);
                        // onMenuAction?.();
                    },
                });
            }
        },
        shouldShow: (_entity) => {
            return canUser(CREATE, PRODUCT);
        },
    });

    menuItemsAcc.push({
        icon: <Edit />,
        label: t("modifier"),
        onClick: (entity) => {
            const url = URLHandler.product.edit(companyIds.id, entity.id, 0);
            // onMenuAction?.();
            navigate(url);
        },
        shouldShow: (entity) => {
            return entity?.canModify ?? false;
        },
    });

    menuItemsAcc.push({
        icon: <Delete />,
        label: t("supprimer"),
        onClick: (entity) => {
            deleteProduct.mutate(entity.id, {
                onSuccess: () => {
                    queryClient.invalidateQueries([productGridKey]);
                    // onMenuAction?.();
                    // setRowMenuAnchor(null);
                },
            });
        },
        shouldShow: (entity) => {
            return entity?.canDelete ?? false;
        },
    });

    return menuItemsAcc;
};
